import React, { useState, useRef, useEffect } from 'react';
import { History, Search, X } from 'lucide-react';
import { SearchQuery, SearchEntityType, SortOrder } from '../../types/search';

interface GoogleLandingPageProps {
  onSearch: (query: SearchQuery) => void;
}

/** Shared with the header search bar */
const RECENT_KEY = 'staketory_recent_searches';
const SUGGESTED = ['director', 'ministry of finance', 'legal affairs'];

function readRecent(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string').slice(0, 5) : [];
  } catch {
    return [];
  }
}

function saveRecent(term: string) {
  try {
    const updated = [term, ...readRecent().filter((s) => s !== term)].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  } catch {}
}

export const GoogleLandingPage: React.FC<GoogleLandingPageProps> = ({ onSearch }) => {
  const [query, setQuery] = useState('');
  const [recent] = useState<string[]>(readRecent);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const runSearch = (term: string) => {
    const q = term.trim();
    if (!q) return;
    saveRecent(q);
    const searchQuery: SearchQuery = {
      q,
      entityTypes: Object.values(SearchEntityType),
      page: 1,
      limit: 20,
      sortBy: 'relevance',
      sortOrder: SortOrder.DESC,
      includeHighlights: true,
    };
    onSearch(searchQuery);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(query);
  };

  const pills = recent.length > 0 ? recent : SUGGESTED;

  return (
    <div className="flex min-h-[calc(100dvh-14rem)] flex-col items-center pt-[10vh] font-ui sm:min-h-[calc(100vh-12rem)] sm:justify-center sm:pt-0">
      <h1 className="sr-only">Search</h1>
      <div className="w-full max-w-xl">
        <form onSubmit={handleSubmit} role="search">
          <div className="group relative">
            <Search
              className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400 transition-colors group-focus-within:text-ink"
              strokeWidth={1.75}
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search anything"
              aria-label="Search"
              enterKeyHint="search"
              autoComplete="off"
              className="block h-14 w-full rounded-full border-0 bg-neutral-100 pl-[3.25rem] pr-14 text-[17px] text-ink placeholder:text-neutral-400 transition-shadow focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/25"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-ink"
              >
                <X className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </button>
            )}
          </div>
        </form>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
          {pills.map((term) => (
            <button
              key={term}
              type="button"
              onClick={() => {
                setQuery(term);
                runSearch(term);
              }}
              className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-full px-3 text-[13px] text-neutral-500 ring-1 ring-inset ring-neutral-200 transition-colors hover:bg-neutral-50 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
              title={recent.length > 0 ? 'Recent search' : 'Try'}
            >
              {recent.length > 0 && <History className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />}
              <span className="truncate">{term}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
